import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle2, HelpCircle, AlertCircle, Loader2 } from 'lucide-react';

import pic1 from '../../assets/rdv/pic1.png'; 
import pic2 from '../../assets/rdv/pic2.png'; 

const RdvSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    details: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false); 
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');     

  const validateForm = () => {
    let newErrors = {};
    if (!formData.name) newErrors.name = "Nom requis";
    if (!formData.email) {
      newErrors.email = "Email requis";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Format email invalide";
    }
    if (!formData.service) newErrors.service = "Sélectionnez un service";
    if (!formData.details) newErrors.details = "Veuillez décrire votre projet";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
     
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.company || 'Non spécifié', 
        service: formData.service,
        message: formData.details,
        date: new Date().toLocaleDateString('fr-FR') 
      };

      const response = await fetch('/api/appointment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', company: '', service: '', details: '' });
      } else {
        setSubmitError(data.message || "Une erreur est survenue lors de l'envoi.");
      }
    } catch (error) {
      console.error("API Error Context:", error);
      setSubmitError("Impossible de joindre le serveur local backend Nodixs. Lancez 'node index.js'.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [
    {
      q: "Comment se déroule le premier appel ?",
      a: "C'est une session de découverte de 15-30 minutes pour comprendre vos enjeux, périmètres et contraintes techniques."
    },
    {
      q: "Quels sont vos délais d'intervention ?",
      a: "Nous pouvons généralement démarrer une mission sous 2 à 3 semaines après validation du devis."
    },
    {
      q: "Mes données sont-elles sécurisées ?",
      a: "La confidentialité est au cœur de notre métier. Toutes les données sont chiffrées et traitées sous accord de non-divulgation (NDA)."
    }
  ];

  return (
    <section className="w-full bg-white py-20 px-6 font-inter border-t border-slate-100 mt-12">
      <div className="max-w-[1200px] mx-auto">
        
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,380px] gap-12 items-start">
          
          <div className="flex flex-col gap-10">
            
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-[#0f172a] text-3xl md:text-4xl font-black mb-4 tracking-tight">
                Réserver un créneau avec nos experts
              </h2>
              <p className="text-slate-500 font-medium max-w-2xl leading-relaxed">
                Discutez de vos besoins en OSINT et Pentest avec notre équipe technique. 
                Obtenez une évaluation personnalisée de votre posture de sécurité.
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-slate-50 p-8 rounded-2xl shadow-md border border-slate-200"
            >
              <AnimatePresence mode="wait">
                {!isSubmitted ? (
                  <motion.form 
                    key="contact-form"
                    onSubmit={handleSubmit} 
                    className="space-y-6"
                  >
                    {/* Error Banner Placement */}
                    {submitError && (
                      <div className="bg-red-50 text-red-600 p-4 rounded-xl flex items-center gap-3 text-sm border border-red-100">
                        <AlertCircle size={18} className="shrink-0" />
                        <p className="font-medium">{submitError}</p>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-[13px] font-bold text-slate-700 mb-2">Nom complet</label>
                        <input 
                          type="text"
                          disabled={isSubmitting}
                          placeholder="Jean Dupont"
                          className={`w-full p-3 bg-slate-50 border rounded-xl outline-none transition-all text-sm ${errors.name ? 'border-red-400' : 'border-slate-200 focus:border-blue-500'} ${isSubmitting && 'opacity-60 pointer-events-none'}`}
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-[13px] font-bold text-slate-700 mb-2">Email professionnel</label>
                        <input 
                          type="email"
                          disabled={isSubmitting}
                          placeholder="jean@entreprise.com"
                          className={`w-full p-3 bg-slate-50 border rounded-xl outline-none transition-all text-sm ${errors.email ? 'border-red-400' : 'border-slate-200 focus:border-blue-500'} ${isSubmitting && 'opacity-60 pointer-events-none'}`}
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-[13px] font-bold text-slate-700 mb-2">Entreprise</label>
                        <input 
                          type="text"
                          disabled={isSubmitting}
                          placeholder="Nom de votre société"
                          className={`w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 transition-all text-sm ${isSubmitting && 'opacity-60 pointer-events-none'}`}
                          value={formData.company}
                          onChange={(e) => setFormData({...formData, company: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-[13px] font-bold text-slate-700 mb-2">Service souhaité</label>
                        <select 
                          disabled={isSubmitting}
                          className={`w-full p-3 bg-slate-50 border rounded-xl outline-none transition-all text-sm ${errors.service ? 'border-red-400' : 'border-slate-200 focus:border-blue-500'} ${isSubmitting && 'opacity-60 pointer-events-none'}`}
                          value={formData.service}
                          onChange={(e) => setFormData({...formData, service: e.target.value})}
                        >
                          <option value="">Sélectionnez un service</option>
                          <option value="osint">OSINT & Investigation</option>
                          <option value="pentest">Tests d'intrusion</option>
                          <option value="audit">Audit de sécurité</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-[13px] font-bold text-slate-700 mb-2">Détails de votre projet</label>
                      <textarea 
                        rows="4"
                        disabled={isSubmitting}
                        placeholder="Décrivez brièvement vos besoins ou le contexte de votre demande..."
                        className={`w-full p-3 bg-slate-50 border rounded-xl outline-none transition-all text-sm resize-none ${errors.details ? 'border-red-400' : 'border-slate-200 focus:border-blue-500'} ${isSubmitting && 'opacity-60 pointer-events-none'}`}
                        value={formData.details}
                        onChange={(e) => setFormData({...formData, details: e.target.value})}
                      />
                    </div>

                    <div className="flex flex-col md:flex-row items-center gap-6 pt-2">
                      <button 
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full md:w-auto bg-[#0f172a] text-white px-8 py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-[#1e3a8a] transition-colors shadow-lg shadow-blue-900/10 disabled:bg-slate-400 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            Envoi en cours... <Loader2 size={15} className="animate-spin" />
                          </>
                        ) : (
                          <>
                            Envoyer la demande <Send size={15} />
                          </>
                        )}
                      </button>
                      <p className="text-[11px] text-slate-400 leading-tight italic">
                        En envoyant ce formulaire, vous acceptez notre politique de confidentialité.
                      </p>
                    </div>
                  </motion.form>
                ) : (
                  <motion.div 
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center justify-center py-10 text-center"
                  >
                    <CheckCircle2 size={48} className="text-blue-500 mb-4" />
                    <h3 className="text-xl font-black text-[#0f172a] mb-2">Demande envoyée</h3>
                    <p className="text-slate-500 text-sm">Un expert technique reviendra vers vous sous 24h.</p>
                    <button 
                      onClick={() => setIsSubmitted(false)}
                      className="mt-6 text-sm text-blue-500 font-bold hover:underline"
                    >
                      Envoyer une autre demande
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="pt-4"
            >
              <img 
                src={pic1} 
                alt="Expertise illustration" 
                className="w-full max-w-[480px] h-auto object-contain mr-auto"
              />
            </motion.div>
          </div>

          <div className="flex flex-col gap-8">
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-slate-50 p-8 rounded-2xl border border-slate-200 shadow-md"
            >
              <div className="flex items-center gap-2 mb-8 text-blue-500">
                <HelpCircle size={18} />
                <span className="text-[12px] font-black uppercase tracking-widest">Questions fréquentes</span>
              </div>
              <div className="space-y-8">
                {faqs.map((faq, i) => (
                  <div key={i} className="group">
                    <h4 className="text-[14px] font-bold text-[#0f172a] mb-2.5 flex items-start gap-2 leading-tight">
                      <span className="text-blue-500 mt-1">▸</span> {faq.q}
                    </h4>
                    <p className="text-[12.5px] text-slate-500 leading-relaxed font-medium pl-5 group-hover:text-slate-600">
                      {faq.a}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <img 
                src={pic2} 
                alt="Technical security" 
                className="w-full h-auto object-cover rounded-2xl shadow-xl border border-white"
              />
            </motion.div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default RdvSection;